import { useMemo } from 'react';
import { useLocaleContext } from '@shared/core/i18n/LocaleContext';
import { milesToKm } from '../milesToMeters';
import { useIsImperialCultures } from './useIsImperialCultures';

const DISTANCE = 'distance';
const TIME = 'time';

const MINUTES = 'minutes';
const MILES = 'miles';
const KILOMETERS = 'kilometers';

export type FormatTravelDistanceInput = Readonly<{
  value: number;
  type: typeof DISTANCE | typeof TIME;
}>;

export type FormatTravelDistanceOutput = Readonly<{
  formatted: string;
  unit: string;
  unitLabel: string;
}>;

/**
 * Hook to format travel distance or time values based on the user's locale and measurement system.
 *
 * - Automatically detects whether the user prefers imperial (miles) or metric (kilometers) units.
 * - Converts distance from miles to kilometers if needed.
 * - Formats the value using locale-aware number formatting.
 * - Returns both the formatted value and a unit label for display (e.g., "mi", "km", "min").
 *
 * ### Distance Formatting
 * - If `type` is `"distance"`:
 *   - Uses `"miles"` for imperial cultures (e.g., US, GB).
 *   - Uses `"kilometers"` for metric cultures.
 *   - Converts miles to kilometers if needed.
 *   - Rounds to 1 decimal place, removes trailing `.0` if whole number.
 *
 * ### Time Formatting
 * - If `type` is `"time"`:
 *   - Uses `"minutes"` as the unit.
 *   - Rounds to the nearest whole number.
 *
 * @returns A memoized function that takes a value and type, and returns:
 * - `formatted`: The locale-formatted number string.
 * - `unit`: The full unit name (`"miles"`, `"kilometers"`, or `"minutes"`).
 * - `unitLabel`: The abbreviated unit label (`"mi"`, `"km"`, or `"min"`).
 */
export const useFormatTravelDistance = () => {
  const { language } = useLocaleContext();
  const isImperialCultures = useIsImperialCultures();

  return useMemo(() => {
    const unitKeyMap: Record<typeof MILES | typeof KILOMETERS | typeof MINUTES, string> = {
      miles: 'mi',
      kilometers: 'km',
      minutes: 'min',
    };

    return ({ value, type }: FormatTravelDistanceInput): FormatTravelDistanceOutput => {
      const unit = type === TIME ? MINUTES : isImperialCultures ? MILES : KILOMETERS;
      const unitLabel = unitKeyMap[unit];

      // Convert miles to kilometers if needed
      const convertedValue = type === DISTANCE && unit === KILOMETERS ? milesToKm(value) : value;

      const formatter = new Intl.NumberFormat(language, {
        minimumFractionDigits: 0,
        maximumFractionDigits: type === TIME ? 0 : 1,
      });

      return {
        formatted: formatter.format(convertedValue),
        unit,
        unitLabel,
      };
    };
  }, [isImperialCultures, language]);
};
